import axios from 'axios';
import {API_URL} from '../config/constants';
import {
    DeclaredChainsDTO,
    RepeatedAskGroupDTO,
    RepeatedAskOrder,
    SafetyRecommendationDTO,
    SafetyRecommendationInvestigationDTO,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationLinkedReportDTO,
    SafetyRecommendationPage,
    SafetyRecommendationRelatedDTO,
    SafetyRecommendationSearchCriteria,
    SafetyRecommendationSourceDTO,
    SafetyRecommendationStatsDTO,
    SafetyRecommendationTaxonomyDTO,
    SafetyRecommendationTaxonomySummaryDTO,
    NtsbMitigationTaxonomyTestResultDTO,
} from '../@types/safety-recommendation';
import {JobExecutionContext} from '../@types/job';
import {getErrorMessage} from '../utils/utils';

const SAFETY_RECOMMENDATIONS_URL = `${API_URL}api/v2/safetyRecommendations`;
const JSON_REQUEST = {withCredentials: true, headers: {Accept: 'application/json'}};

/** How a sync fetches: changed since the last one, everything, or every recommendation still open. */
export type SyncMode = 'changed' | 'full' | 'open';

/** The local copy of safety recommendations: search, one recommendation, and each source's jobs. */
class SafetyRecommendationService {

    /** One page of the recommendations matching the filters. */
    async search(
        criteria: SafetyRecommendationSearchCriteria, page: number, size: number, sort: string
    ): Promise<SafetyRecommendationPage> {
        try {
            const response = await axios.get<SafetyRecommendationPage>(
                `${SAFETY_RECOMMENDATIONS_URL}?${this.query(criteria, {page, size, sort})}`, JSON_REQUEST);
            if (!Array.isArray(response.data?.content)) {
                throw new Error('The server did not return recommendations. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to search recommendations');
        }
    }

    /** The counts over everything the filters match, for the charts. */
    async summary(criteria: SafetyRecommendationSearchCriteria): Promise<SafetyRecommendationStatsDTO> {
        try {
            const response = await axios.get<SafetyRecommendationStatsDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/summary?${this.query(criteria)}`, JSON_REQUEST);
            if (typeof response.data?.total !== 'number') {
                throw new Error('The server did not return the counts. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to count recommendations');
        }
    }

    /**
     * The matching recommendations counted by kind of fix and decade. Asked for only when the grid
     * is on screen: it is a wider aggregate than the counts the charts run on.
     */
    async taxonomySummary(criteria: SafetyRecommendationSearchCriteria):
        Promise<SafetyRecommendationTaxonomySummaryDTO> {
        try {
            const response = await axios.get<SafetyRecommendationTaxonomySummaryDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/taxonomySummary?${this.query(criteria)}`, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to count recommendations by taxonomy');
        }
    }

    /**
     * Asks the recommendations agent a question. Unlike the shared chat endpoint, this one queries
     * the corpus rather than being handed a page of it, so it can count; the answer carries the
     * queries it ran so a figure can be traced.
     */
    async ask(question: string, history: {fromUser: boolean; text: string}[] = []):
        Promise<SafetyRecommendationInvestigationDTO> {
        try {
            const response = await axios.post<SafetyRecommendationInvestigationDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/ask`, {question, history}, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to answer the question');
        }
    }

    /**
     * The steps taken so far, and the answer once there is one.
     *
     * Polled rather than streamed: the development proxy buffers server-sent events, so a streaming
     * version of this would not work locally.
     */
    async askProgress(id: string): Promise<SafetyRecommendationInvestigationDTO> {
        try {
            const response = await axios.get<SafetyRecommendationInvestigationDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/ask/${encodeURIComponent(id)}`, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to follow the question');
        }
    }

    /**
     * Groups of recommendations asking for the same change.
     *
     * @param order how to rank them: most recently asked again, longest running, or most repeated
     * @param limit how many to return. Only the first screenful is titled by the model, so asking
     *              for everything, as the export does, costs nothing extra
     */
    async repeatedAsks(order: RepeatedAskOrder = 'NEWEST', limit = 50): Promise<RepeatedAskGroupDTO[]> {
        try {
            const response = await axios.get<RepeatedAskGroupDTO[]>(
                `${SAFETY_RECOMMENDATIONS_URL}/repeatedAsks?limit=${limit}&order=${order}`, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load repeated asks');
        }
    }

    /**
     * The chains the issuer itself declared: supersessions, or reiterations.
     *
     * A few hundred against nearly six thousand recommendations, so the whole set comes at once.
     */
    async declaredChains(supersedes: boolean, source = 'NTSB'): Promise<DeclaredChainsDTO> {
        try {
            const response = await axios.get<DeclaredChainsDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/declaredChains?source=${encodeURIComponent(source)}`
                + `&supersedes=${supersedes}`, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the declared chains');
        }
    }

    /** One recommendation with its addressees, cases, supersessions and letters. */
    async get(id: number): Promise<SafetyRecommendationDTO> {
        try {
            const response = await axios.get<SafetyRecommendationDTO>(`${SAFETY_RECOMMENDATIONS_URL}/${id}`, JSON_REQUEST);
            if (typeof response.data?.id !== 'number') {
                throw new Error('The server did not return the recommendation. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the recommendation', `Recommendation ${id} is not in the local copy`);
        }
    }

    /** Every letter between the issuer and any addressee about one recommendation, oldest first, in full. */
    async getCorrespondence(recommendation: Pick<SafetyRecommendationDTO, 'id' | 'number'>): Promise<SafetyRecommendationLetterDTO[]> {
        try {
            const response = await axios.get<SafetyRecommendationLetterDTO[]>(
                `${SAFETY_RECOMMENDATIONS_URL}/${recommendation.id}/correspondence`, JSON_REQUEST);
            if (!Array.isArray(response.data)) {
                throw new Error('The server did not return the correspondence. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the correspondence',
                `Recommendation ${recommendation.number} is not in the local copy`);
        }
    }

    /** The ISAM reports the caller may see whose investigation issued or reiterated the recommendation. */
    async getReports(id: number): Promise<SafetyRecommendationLinkedReportDTO[]> {
        try {
            const response = await axios.get<SafetyRecommendationLinkedReportDTO[]>(
                `${SAFETY_RECOMMENDATIONS_URL}/${id}/reports`, JSON_REQUEST);
            if (!Array.isArray(response.data)) {
                throw new Error('The server did not return the tied reports. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the tied reports', `Recommendation ${id} is not in the local copy`);
        }
    }

    /** What supersedes the recommendation, what it supersedes, and its case fellows. */
    async getRelated(id: number): Promise<SafetyRecommendationRelatedDTO> {
        try {
            const response = await axios.get<SafetyRecommendationRelatedDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/${id}/related`, JSON_REQUEST);
            if (!Array.isArray(response.data?.supersededBy)) {
                throw new Error('The server did not return the related recommendations. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the related recommendations',
                `Recommendation ${id} is not in the local copy`);
        }
    }

    /** Every source, with how much is held from it and whether a job is running. */
    async getSources(): Promise<SafetyRecommendationSourceDTO[]> {
        try {
            const response = await axios.get<SafetyRecommendationSourceDTO[]>(
                `${SAFETY_RECOMMENDATIONS_URL}/sources`, JSON_REQUEST);
            if (!Array.isArray(response.data)) {
                throw new Error('The server did not return the sources. It may need restarting.');
            }
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the sources');
        }
    }

    /** Starts a sync of one source. Super user only. */
    async startSync(source: string, mode: SyncMode): Promise<JobExecutionContext> {
        try {
            const response = await axios.post<JobExecutionContext>(
                `${SAFETY_RECOMMENDATIONS_URL}/sources/${source}/sync?mode=${mode}`, null, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.jobFailure(err, `Failed to start the ${source} sync`);
        }
    }

    /**
     * Starts an indexing run for one source: whatever needs embedding for meaning search. Super
     * user only.
     */
    /**
     * The taxonomy recommendations are tagged with. Static reference data, so a screen reads it once.
     */
    async getTaxonomy(): Promise<SafetyRecommendationTaxonomyDTO> {
        try {
            const response = await axios.get<SafetyRecommendationTaxonomyDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/taxonomy`, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to load the taxonomy');
        }
    }

    /**
     * Starts a tagging run over a source's recommendations. Super users only.
     *
     * @param retag true to tag everything again, replacing our own tags and leaving the source's
     *              coding alone; false to tag only what is untagged or tagged from older text
     */
    async startTaxonomyTagging(source: string, retag = false): Promise<JobExecutionContext> {
        try {
            const response = await axios.post<JobExecutionContext>(
                `${SAFETY_RECOMMENDATIONS_URL}/sources/${source}/taxonomyTag?retag=${retag}`, null, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.jobFailure(err, `Failed to start tagging ${source} recommendations`);
        }
    }

    /** Tries tagging one recommendation and says what the model chose, saving nothing. Super users only. */
    async testTaxonomyTagging(id: number): Promise<NtsbMitigationTaxonomyTestResultDTO> {
        try {
            const response = await axios.post<NtsbMitigationTaxonomyTestResultDTO>(
                `${SAFETY_RECOMMENDATIONS_URL}/${id}/taxonomyTest`, null, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.readable(err, 'Failed to try tagging the recommendation');
        }
    }

    async startIndex(source: string): Promise<JobExecutionContext> {
        try {
            const response = await axios.post<JobExecutionContext>(
                `${SAFETY_RECOMMENDATIONS_URL}/sources/${source}/index`, null, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.jobFailure(err, `Failed to start the ${source} index`);
        }
    }

    /** Starts a rebuild of one source from the records already held. Super user only. */
    async startRebuild(source: string): Promise<JobExecutionContext> {
        try {
            const response = await axios.post<JobExecutionContext>(
                `${SAFETY_RECOMMENDATIONS_URL}/sources/${source}/rebuild`, null, JSON_REQUEST);
            return response.data;
        } catch (err: unknown) {
            throw this.jobFailure(err, `Failed to start the ${source} rebuild`);
        }
    }

    /** The filters as query parameters, leaving out everything empty so the URL says what was asked. */
    private query(criteria: SafetyRecommendationSearchCriteria, paging?: {page: number; size: number; sort: string}): string {
        const params = new URLSearchParams();
        if (criteria.text?.trim()) {
            params.append('text', criteria.text.trim());
        }
        (criteria.sources ?? []).forEach((source) => params.append('sources', source));
        (criteria.statusGroups ?? []).forEach((group) => params.append('statusGroups', group));
        if (criteria.addressedTo && criteria.addressedTo !== 'ANY') {
            params.append('addressedTo', criteria.addressedTo);
        }
        if (criteria.issuedFromYear) {
            params.append('issuedFromYear', String(criteria.issuedFromYear));
        }
        if (criteria.issuedToYear) {
            params.append('issuedToYear', String(criteria.issuedToYear));
        }
        if (criteria.includeSuperseded) {
            params.append('includeSuperseded', 'true');
        }
        if (criteria.caseNumber) {
            params.append('caseNumber', criteria.caseNumber);
        }
        (criteria.taxonomyLeafIds ?? []).forEach((leafId) => params.append('taxonomyLeafIds', String(leafId)));
        if (criteria.includeLetters) {
            params.append('includeLetters', 'true');
        }
        if (paging) {
            params.append('page', String(paging.page));
            params.append('size', String(paging.size));
            params.append('sort', paging.sort);
        }
        return params.toString();
    }

    /** An error the UI can show: the shape guard as thrown, a 404 in the caller's words, anything else with the server's. */
    private readable(err: unknown, failed: string, notFound?: string): Error {
        if (!axios.isAxiosError(err)) {
            return err instanceof Error ? err : new Error(failed);
        }
        if (notFound && err.response?.status === 404) {
            return new Error(notFound);
        }
        return new Error(`${failed}: ${err.response ? getErrorMessage(err) : err.message}`);
    }

    /** A sync or rebuild failure: a refused caller and a running job are both worth saying plainly. */
    private jobFailure(err: unknown, failed: string): Error {
        if (!axios.isAxiosError(err)) {
            return err instanceof Error ? err : new Error(failed);
        }
        if (err.response?.status === 403) {
            return new Error('Only a super user can sync safety recommendations');
        }
        if (err.response?.status === 409) {
            return new Error(getErrorMessage(err));
        }
        return new Error(`${failed}: ${err.response ? getErrorMessage(err) : err.message}`);
    }
}

export default new SafetyRecommendationService();
